import { useEffect, useState } from 'react';
import { FiX, FiShare2, FiHeart } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { collection, query, where, onSnapshot, addDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { scrollToId } from '../utils/scroll';

const CATEGORIES = [
  'All',
  'African word a day',
  'African proverbs',
  "Happy Mother's Day",
  "Happy Father's Day",
  'Happy birthday',
  'African humour',
];

const galleryItems = [
  {
    id: 1,
    title: 'African Word of the Day',
    description: 'A short animated lesson teaching a new African word, its meaning, and pronunciation.',
    src: '/1.mov',
    categories: ['African word a day'],
  },
  {
    id: 2,
    title: 'Proverbs & Wisdom',
    description: 'Timeless African proverbs brought to life through fun, memorable animation.',
    src: '/2.mov',
    categories: ['African proverbs'],
  },
  {
    id: 3,
    title: "Happy Mother's Day",
    description: 'A heartfelt animated tribute celebrating moms everywhere.',
    src: '/3.mov',
    categories: ["Happy Mother's Day"],
  },
  {
    id: 4,
    title: "Happy Father's Day",
    description: 'A playful animation celebrating dads and their adventures.',
    src: '/4.mov',
    categories: ["Happy Father's Day"],
  },
  {
    id: 5,
    title: 'Happy Birthday Wishes',
    description: 'A joyful animated birthday greeting, African-style.',
    src: '/1.mov',
    categories: ['Happy birthday'],
  },
  {
    id: 6,
    title: 'African Humour',
    description: 'Light-hearted animated sketches celebrating African humour and everyday life.',
    src: '/2.mov',
    categories: ['African humour'],
  },
  {
    id: 7,
    title: 'Word of the Day: Community Spirit',
    description: 'Another daily word lesson, paired with a touch of humour kids will love.',
    src: '/3.mov',
    categories: ['African word a day', 'African humour'],
  },
  {
    id: 8,
    title: 'A Proverb Worth Celebrating',
    description: 'Wisdom and celebration combined for a special birthday lesson.',
    src: '/4.mov',
    categories: ['African proverbs', 'Happy birthday'],
  },
];

const handleShare = async (item) => {
  const shareData = {
    title: `${item.title} — Eunbro`,
    text: item.description,
    url: `${window.location.origin}/#videos`,
  };

  if (navigator.share) {
    try {
      await navigator.share(shareData);
    } catch (err) {
      if (err.name !== 'AbortError') {
        toast.error('Could not share this video.');
      }
    }
    return;
  }

  if (navigator.clipboard) {
    try {
      await navigator.clipboard.writeText(shareData.url);
      toast.success('Link copied to clipboard!');
    } catch (err) {
      toast.error('Could not copy the link.');
    }
    return;
  }

  toast.error('Sharing is not supported on this browser.');
};

const handleShareToWhatsApp = (item) => {
  const text = `Check out "${item.title}" on Eunbro — ${item.description} ${window.location.origin}/#videos`;
  const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
  window.open(url, '_blank', 'noopener,noreferrer');
};

const GalleryCard = ({ item, showFavorite, isFavorited, onToggleFavorite, onSubscribe }) => {
  const [loaded, setLoaded] = useState(false);

  return (
    <div className="group relative bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100">
      <div className="relative overflow-hidden pt-[56.25%] bg-gray-100">
        {!loaded && (
          <div className="absolute inset-0 flex items-center justify-center animate-pulse bg-gray-200">
            <svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        )}
        <video
          src={item.src}
          autoPlay
          loop
          muted
          playsInline
          onCanPlay={() => setLoaded(true)}
          className={`absolute top-0 left-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ${
            loaded ? 'opacity-100' : 'opacity-0'
          }`}
        />

        <div className="absolute top-3 right-3 z-10 flex gap-2">
          <button
            type="button"
            onClick={() => handleShareToWhatsApp(item)}
            aria-label={`Share ${item.title} on WhatsApp`}
            className="w-8 h-8 rounded-full bg-black/40 hover:bg-[#25D366] backdrop-blur-sm text-white flex items-center justify-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
          >
            <FaWhatsapp className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => handleShare(item)}
            aria-label={`Share ${item.title}`}
            className="w-8 h-8 rounded-full bg-black/40 hover:bg-black/60 backdrop-blur-sm text-white flex items-center justify-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
          >
            <FiShare2 className="w-4 h-4" />
          </button>
          {showFavorite && (
            <button
              type="button"
              onClick={() => onToggleFavorite(item)}
              aria-label={isFavorited ? `Remove ${item.title} from favorites` : `Add ${item.title} to favorites`}
              aria-pressed={isFavorited}
              className="w-8 h-8 rounded-full bg-black/40 hover:bg-black/60 backdrop-blur-sm text-white flex items-center justify-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              <FiHeart className={`w-4 h-4 ${isFavorited ? 'fill-current text-red-500' : ''}`} />
            </button>
          )}
        </div>
      </div>
      <div className="p-4">
        <h4 className="font-bold text-gray-900 mb-1">{item.title}</h4>
        <p className="text-sm text-gray-600 mb-3">{item.description}</p>
        <div className="flex flex-wrap gap-1.5 mb-3">
          {item.categories.map((category) => (
            <span
              key={category}
              className="text-xs font-medium px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700"
            >
              {category}
            </span>
          ))}
        </div>
        {isFavorited && (
          <button
            type="button"
            onClick={onSubscribe}
            className="text-sm font-medium text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
          >
            Subscribe to unlock daily content
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

const WorkGalleryModal = ({ isOpen, onClose }) => {
  const [activeCategory, setActiveCategory] = useState('All');
  const [favorites, setFavorites] = useState(new Map()); // videoId -> favorite doc id
  const { user } = useAuth();

  useEffect(() => {
    if (!isOpen) return undefined;

    setActiveCategory('All');
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen || !user) {
      setFavorites(new Map());
      return undefined;
    }

    const q = query(collection(db, 'favorites'), where('userId', '==', user.uid));
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const next = new Map();
        snapshot.docs.forEach((favDoc) => next.set(favDoc.data().videoId, favDoc.id));
        setFavorites(next);
      },
      (err) => {
        console.error('Failed to load favorites', err);
      }
    );

    return () => unsubscribe();
  }, [isOpen, user]);

  const toggleFavorite = async (item) => {
    if (!user) return;

    const withTimeout = (promise) =>
      Promise.race([
        promise,
        new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 12000)),
      ]);

    try {
      const existingFavoriteId = favorites.get(item.id);
      if (existingFavoriteId) {
        await withTimeout(deleteDoc(doc(db, 'favorites', existingFavoriteId)));
        toast.success('Removed from favorites');
      } else {
        await withTimeout(
          addDoc(collection(db, 'favorites'), {
            userId: user.uid,
            videoId: item.id,
            videoTitle: item.title,
            createdAt: serverTimestamp(),
          })
        );
        toast.success('Added to favorites!');
      }
    } catch (err) {
      toast.error('Could not update favorites. Please try again.');
    }
  };

  const handleSubscribe = () => {
    onClose();
    scrollToId('plans');
  };

  if (!isOpen) return null;

  const visibleCategories = user ? [...CATEGORIES, 'Favorites'] : CATEGORIES;

  const filteredItems =
    activeCategory === 'Favorites'
      ? galleryItems.filter((item) => favorites.has(item.id))
      : activeCategory === 'All'
      ? galleryItems
      : galleryItems.filter((item) => item.categories.includes(activeCategory));

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 px-4 py-8"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-6 py-5 border-b border-gray-100">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Our Work</h2>
            <p className="text-gray-500 text-sm mt-1">
              Daily African language lessons, proverbs, and celebrations brought to life through animation.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close gallery"
            className="text-gray-400 hover:text-gray-600 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 rounded-full p-1 flex-shrink-0"
          >
            <FiX className="w-6 h-6" />
          </button>
        </div>

        <div className="px-6 pt-4 flex flex-wrap gap-2">
          {visibleCategories.map((category) => (
            <button
              key={category}
              type="button"
              onClick={() => setActiveCategory(category)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 flex items-center gap-1.5 ${
                activeCategory === category
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {category === 'Favorites' && <FiHeart className="w-3.5 h-3.5" />}
              {category}
            </button>
          ))}
        </div>

        {!user && (
          <p className="px-6 pt-4 text-sm text-gray-500">
            Log in to save videos to your favorites.
          </p>
        )}

        <div className="p-6 overflow-y-auto">
          {filteredItems.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredItems.map((item) => (
                <GalleryCard
                  key={item.id}
                  item={item}
                  showFavorite={Boolean(user)}
                  isFavorited={favorites.has(item.id)}
                  onToggleFavorite={toggleFavorite}
                  onSubscribe={handleSubscribe}
                />
              ))}
            </div>
          ) : activeCategory === 'Favorites' ? (
            <p className="text-center text-gray-500 py-12">
              No favorites yet — tap the heart on any video to save it here.
            </p>
          ) : (
            <p className="text-center text-gray-500 py-12">No videos in this category yet.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkGalleryModal;
